import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Check, ChevronDown, Search } from 'lucide-react';

import { PropertyOptionIcon } from './PropertyOptionIcon';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../../components/ui/Popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/Select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../../components/ui/Tooltip';
import { PropertyOption } from '../../../types/shared';
import { cn } from '../../../utils';

interface Props {
    readonly value?: string;
    readonly onChange: (value: string) => void;
    readonly options: PropertyOption[];
    readonly searchable?: boolean;
    readonly multiselect?: boolean;
    readonly showSelectionIndicator?: boolean;
    readonly size?: 'default' | 'compact';
    readonly testId?: string;
    readonly allLabel?: string;
    readonly searchPlaceholder?: string;
    readonly emptyText?: string;
    readonly triggerClassName?: string;
    readonly contentClassName?: string;
}

const ALL_VALUE = 'all';

const OverflowAwareLabel: FC<{
    text: string;
    className?: string;
    testId?: string;
}> = ({ text, className, testId }) => {
    const labelRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const measureOverflow = useCallback(() => {
        const label = labelRef.current;
        if (!label) return;

        const nextIsOverflowing = label.scrollWidth > label.clientWidth + 1;
        setIsOverflowing(nextIsOverflowing);
        if (!nextIsOverflowing) setTooltipOpen(false);
    }, []);

    useEffect(() => {
        measureOverflow();

        const label = labelRef.current;
        if (!label || typeof ResizeObserver === 'undefined') return;

        const resizeObserver = new ResizeObserver(measureOverflow);
        resizeObserver.observe(label);

        return () => resizeObserver.disconnect();
    }, [measureOverflow, text]);

    return (
        <Tooltip
            open={tooltipOpen}
            onOpenChange={(open) => setTooltipOpen(isOverflowing && open)}
        >
            <TooltipTrigger asChild>
                <span
                    ref={labelRef}
                    className={cn('truncate', className)}
                    data-testid={testId}
                >
                    {text}
                </span>
            </TooltipTrigger>
            {isOverflowing && (
                <TooltipContent side="top" align="start" className="z-[130]">
                    {text}
                </TooltipContent>
            )}
        </Tooltip>
    );
};

export const PropertyFilterSelect: FC<Props> = ({
    value,
    onChange,
    options,
    searchable = false,
    multiselect = false,
    showSelectionIndicator = true,
    size = 'default',
    testId,
    allLabel = 'Усі значення',
    searchPlaceholder = 'Пошук значень...',
    emptyText = 'Нічого не знайдено',
    triggerClassName,
    contentClassName,
}) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const selectedValue = value ?? ALL_VALUE;

    const selectedValues = useMemo(
        () =>
            selectedValue === ALL_VALUE
                ? []
                : selectedValue
                      .split(',')
                      .map((item) => item.trim())
                      .filter(Boolean),
        [selectedValue],
    );

    const selectedValuesSet = useMemo(
        () => new Set(selectedValues),
        [selectedValues],
    );

    const selectedOption = options.find(
        (option) => option.slug === selectedValue,
    );
    const isCompact = size === 'compact';
    const sizeTriggerClassName = isCompact
        ? 'h-9 px-2.5 py-1.5 text-sm [&_svg]:h-3.5 [&_svg]:w-3.5'
        : undefined;
    const customTriggerClassName = isCompact
        ? 'h-9 gap-1.5 px-2.5 py-1.5 text-sm'
        : 'h-10 gap-2 px-3 py-2 text-base';
    const searchInputClassName = isCompact
        ? 'h-9 pl-8 text-sm'
        : 'pl-8 text-base';
    const optionClassName = cn(
        isCompact ? 'py-1.5 pr-2 text-sm' : 'py-1.5 pr-2 text-base',
        showSelectionIndicator ? 'pl-8' : 'pl-2',
    );

    const selectedOptions = useMemo(
        () => options.filter((option) => selectedValuesSet.has(option.slug)),
        [options, selectedValuesSet],
    );

    const selectedLabel = multiselect
        ? selectedOptions.length === 0
            ? allLabel
            : selectedOptions.length <= 2
              ? selectedOptions.map((option) => option.name).join(', ')
              : `${selectedOptions.length} обрано`
        : (selectedOption?.name ?? allLabel);

    const filteredOptions = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();

        if (!query) return options;

        return options.filter((option) =>
            `${option.badge ?? ''} ${option.name} ${option.slug}`
                .toLowerCase()
                .includes(query),
        );
    }, [options, searchTerm]);

    if (!searchable && !multiselect) {
        return (
            <TooltipProvider delayDuration={250} skipDelayDuration={100}>
                <Select value={selectedValue} onValueChange={onChange}>
                    <SelectTrigger
                        className={cn(sizeTriggerClassName, triggerClassName)}
                        data-testid={testId ? `${testId}-trigger` : undefined}
                    >
                        <SelectValue placeholder={allLabel}>
                            {selectedOption ? (
                                <span className="flex min-w-0 items-center gap-2">
                                    {selectedOption.badge && (
                                        <Badge
                                            className="max-w-[45%] shrink-0 px-2 py-0 text-[11px]"
                                            data-testid={
                                                testId
                                                    ? `${testId}-selected-badge`
                                                    : undefined
                                            }
                                        >
                                            {selectedOption.badge}
                                        </Badge>
                                    )}
                                    <OverflowAwareLabel
                                        text={selectedOption.name}
                                        className="min-w-0 flex-1"
                                        testId={
                                            testId
                                                ? `${testId}-selected-name`
                                                : undefined
                                        }
                                    />
                                </span>
                            ) : (
                                allLabel
                            )}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent className={contentClassName}>
                        <SelectItem
                            className={optionClassName}
                            value={ALL_VALUE}
                            data-testid={
                                testId
                                    ? `${testId}-option-${ALL_VALUE}`
                                    : undefined
                            }
                        >
                            {allLabel}
                        </SelectItem>
                        {options.map((option) => (
                            <SelectItem
                                key={option.id}
                                className={optionClassName}
                                value={option.slug}
                                data-testid={
                                    testId
                                        ? `${testId}-option-${option.slug}`
                                        : undefined
                                }
                            >
                                <span className="flex items-center gap-2">
                                    {option.badge && (
                                        <Badge
                                            className="shrink-0 px-2 py-0 text-[11px]"
                                            data-testid={
                                                testId
                                                    ? `${testId}-option-${option.slug}-badge`
                                                    : undefined
                                            }
                                        >
                                            {option.badge}
                                        </Badge>
                                    )}
                                    <PropertyOptionIcon
                                        iconKey={option.icon_key}
                                        label={option.name}
                                        className="h-5 w-5"
                                    />
                                    <OverflowAwareLabel
                                        text={option.name}
                                        className="min-w-0 flex-1"
                                        testId={
                                            testId
                                                ? `${testId}-option-${option.slug}-name`
                                                : undefined
                                        }
                                    />
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </TooltipProvider>
        );
    }

    const serializeMultiValue = (values: Set<string>) => {
        const orderedValues = options
            .map((option) => option.slug)
            .filter((slug) => values.has(slug));

        return orderedValues.length > 0 ? orderedValues.join(',') : ALL_VALUE;
    };

    const toggleMultiValue = (slug: string) => {
        const nextValues = new Set(selectedValuesSet);

        if (nextValues.has(slug)) {
            nextValues.delete(slug);
        } else {
            nextValues.add(slug);
        }

        onChange(serializeMultiValue(nextValues));
    };

    const clearMultiValue = () => {
        onChange(ALL_VALUE);
        setSearchTerm('');
    };

    const selectValue = (nextValue: string) => {
        onChange(nextValue);
        setSearchTerm('');
        setOpen(false);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    return (
        <TooltipProvider delayDuration={250} skipDelayDuration={100}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid={testId ? `${testId}-trigger` : undefined}
                        className={cn(
                            'flex w-full items-center rounded-xl border border-input bg-background text-start ring-offset-background placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                            customTriggerClassName,
                            triggerClassName,
                        )}
                    >
                        <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
                            {!multiselect && selectedOption?.badge && (
                                <Badge
                                    className="max-w-[45%] shrink-0 px-2 py-0 text-[11px]"
                                    data-testid={
                                        testId
                                            ? `${testId}-selected-badge`
                                            : undefined
                                    }
                                >
                                    {selectedOption.badge}
                                </Badge>
                            )}
                            {selectedOption ? (
                                <OverflowAwareLabel
                                    text={selectedLabel}
                                    className="min-w-0 flex-1"
                                    testId={
                                        testId
                                            ? `${testId}-selected-name`
                                            : undefined
                                    }
                                />
                            ) : (
                                <span className="truncate">
                                    {selectedLabel}
                                </span>
                            )}
                        </span>
                        <ChevronDown
                            className={cn(
                                'shrink-0 opacity-50',
                                isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4',
                            )}
                        />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    className={cn(
                        'w-[var(--radix-popover-trigger-width)] overflow-hidden p-0',
                        contentClassName,
                    )}
                    data-testid={testId ? `${testId}-content` : undefined}
                    align="start"
                    onPointerDownOutside={(event) => {
                        const target = event.target as Node | null;

                        if (
                            inputRef.current &&
                            target &&
                            inputRef.current.contains(target)
                        ) {
                            event.preventDefault();
                            return;
                        }

                        if (document.activeElement === inputRef.current) {
                            event.preventDefault();
                        }
                    }}
                >
                    {searchable && (
                        <div
                            className="sticky top-0 z-10 border-b bg-background p-2"
                            onPointerDown={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <div className="relative">
                                <Search className="absolute top-2.5 left-2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    ref={inputRef}
                                    data-testid={
                                        testId
                                            ? `${testId}-search-input`
                                            : undefined
                                    }
                                    placeholder={searchPlaceholder}
                                    className={searchInputClassName}
                                    value={searchTerm}
                                    onChange={handleInputChange}
                                    onKeyDown={(event) =>
                                        event.stopPropagation()
                                    }
                                    onClick={(event) => event.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}

                    <div
                        className="max-h-[300px] touch-pan-y overflow-y-auto overscroll-contain p-1"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                        onTouchMove={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            data-testid={
                                testId
                                    ? `${testId}-option-${ALL_VALUE}`
                                    : undefined
                            }
                            className={cn(
                                'relative flex w-full cursor-default items-center rounded-xl outline-none select-none hover:bg-accent hover:text-accent-foreground',
                                optionClassName,
                            )}
                            aria-pressed={
                                multiselect
                                    ? selectedValues.length === 0
                                    : selectedValue === ALL_VALUE
                            }
                            onClick={
                                multiselect
                                    ? clearMultiValue
                                    : () => selectValue(ALL_VALUE)
                            }
                        >
                            {showSelectionIndicator && (
                                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                    <Check
                                        className={cn(
                                            'h-4 w-4',
                                            multiselect
                                                ? selectedValues.length === 0
                                                    ? 'opacity-100'
                                                    : 'opacity-0'
                                                : selectedValue === ALL_VALUE
                                                  ? 'opacity-100'
                                                  : 'opacity-0',
                                        )}
                                    />
                                </span>
                            )}
                            <span className="min-w-0 flex-1 truncate text-left">
                                {allLabel}
                            </span>
                        </button>

                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    type="button"
                                    key={option.id}
                                    data-testid={
                                        testId
                                            ? `${testId}-option-${option.slug}`
                                            : undefined
                                    }
                                    className={cn(
                                        'relative flex w-full cursor-default items-center rounded-xl outline-none select-none hover:bg-accent hover:text-accent-foreground',
                                        optionClassName,
                                    )}
                                    aria-pressed={
                                        multiselect
                                            ? selectedValuesSet.has(option.slug)
                                            : selectedValue === option.slug
                                    }
                                    onClick={() =>
                                        multiselect
                                            ? toggleMultiValue(option.slug)
                                            : selectValue(option.slug)
                                    }
                                >
                                    {showSelectionIndicator && (
                                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                            <Check
                                                className={cn(
                                                    'h-4 w-4',
                                                    multiselect
                                                        ? selectedValuesSet.has(
                                                              option.slug,
                                                          )
                                                            ? 'opacity-100'
                                                            : 'opacity-0'
                                                        : selectedValue ===
                                                            option.slug
                                                          ? 'opacity-100'
                                                          : 'opacity-0',
                                                )}
                                            />
                                        </span>
                                    )}
                                    <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
                                        {option.badge && (
                                            <Badge
                                                className="shrink-0 px-2 py-0 text-[11px]"
                                                data-testid={
                                                    testId
                                                        ? `${testId}-option-${option.slug}-badge`
                                                        : undefined
                                                }
                                            >
                                                {option.badge}
                                            </Badge>
                                        )}
                                        <PropertyOptionIcon
                                            iconKey={option.icon_key}
                                            label={option.name}
                                            className="h-5 w-5"
                                        />
                                        <OverflowAwareLabel
                                            text={option.name}
                                            className="min-w-0 flex-1"
                                            testId={
                                                testId
                                                    ? `${testId}-option-${option.slug}-name`
                                                    : undefined
                                            }
                                        />
                                    </span>
                                </button>
                            ))
                        ) : (
                            <div className="py-4 text-center text-sm text-muted-foreground">
                                {emptyText}
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </TooltipProvider>
    );
};
